import { Button, Card, Group, Stack, Text } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { QUICK_REFERENCE_DOC_ORDER, QUICK_REFERENCE_DOCS, PARSED_QUICK_REFERENCE_DOCS } from './quickReferenceContent';
import { Markdown } from './markdown';
import './quickReference.css';

/** Printable per-role quick-reference cards (docs/quick-reference/*.md via
 * quickReferenceContent.ts's build-time bundle) — the "laminate this and
 * stick it at your station" condensed counterpart to the full guides
 * HelpCentre.tsx renders. Reached from the Help Centre's "Quick reference"
 * tab (see HelpCentre.tsx) rather than its own route: there is nothing here
 * that needs deep-linking or HELP_MAP wiring, and staying inside /help keeps
 * the print CSS's `.zs-qr-print-area` boundary the only new concept, rather
 * than also standing up a route + route test.
 *
 * Printing: the single **Print all cards** button calls `window.print()`
 * directly — no print preview UI of our own, the browser's native dialog is
 * the affordance. quickReference.css's `@media print` rules do the actual
 * work: everything outside `.zs-qr-print-area` (app shell header/nav, the
 * tab strip, this component's own intro text and button) is hidden, and each
 * `.zs-qr-card` gets its own printed page. */
export function QuickReference() {
  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4} style={{ maxWidth: 560 }}>
          <Text size="sm">
            One printable page per role — the daily happy path, the handful of "if it goes wrong" moments that
            matter, and who to ask. Pin the one for your job at your station.
          </Text>
          <Text size="sm" c="dimmed">
            These are condensed from the Full guides tab — read that for anything not covered here.
          </Text>
        </Stack>
        <Button leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
          Print all cards
        </Button>
      </Group>

      <div className="zs-qr-print-area">
        <Stack gap="xl">
          {QUICK_REFERENCE_DOC_ORDER.map((docId) => {
            const doc = QUICK_REFERENCE_DOCS[docId];
            return (
              // `Card` for the on-screen/printed card frame (withBorder — the
              // "presentable on screen AND print" ask); `.zs-qr-card` stays the
              // class quickReference.css's `@media print` pagination targets
              // and QuickReference.test.tsx pins — Card just adds its own
              // Mantine classes alongside it, same element (a `div`) either way.
              <Card key={doc.id} className="zs-qr-card" withBorder radius="md" p="lg">
                {/* Brand appears in chrome only (branded-neutral ruling,
                 * 2026-08-24) — one small mark per card, not per section, and
                 * inside `.zs-qr-card` so it survives onto every printed page
                 * (each card is its own page — see quickReference.css). */}
                <Group gap={6} mb="sm">
                  <img src="/brand/zebra-mark.png" alt="" width={16} height={16} style={{ borderRadius: 3 }} />
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    Zebra Stock — Quick reference
                  </Text>
                </Group>
                <Markdown content={doc.content} headings={PARSED_QUICK_REFERENCE_DOCS[docId].headings} />
              </Card>
            );
          })}
        </Stack>
      </div>
    </Stack>
  );
}
